/**
 * Cliente HTTP del tablón de NOTAMs — Fase 10a.
 *
 * `GET /api/notams/board` es público — el frontend lo llama con
 * `credentials: 'include'` para que el backend pueda distinguir
 * post-its privados visibles para el caller logueado. El resto de
 * endpoints (POST/PATCH/DELETE) requieren sesión.
 */

import type {
  Notam,
  NotamBoardResponse,
  NotamPayload,
  NotamPostit,
  NotamPostitPayload,
  PilotSummary,
} from '@51esc/shared';

export type FetchBoardResult =
  | { ok: true; data: NotamBoardResponse }
  | { ok: false; message: string };

export type PostitResult =
  | { ok: true; data: NotamPostit }
  | { ok: false; message: string };

export type NotamResult =
  | { ok: true; data: Notam }
  | { ok: false; message: string };

export type DeleteResult =
  | { ok: true }
  | { ok: false; message: string };

export type FetchPilotsResult =
  | { ok: true; data: PilotSummary[] }
  | { ok: false; message: string };

// ─── Helpers ──────────────────────────────────────────────────────────

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body && typeof body.error === 'string') return body.error;
  } catch {
    /* swallow */
  }
  return `El servidor respondió con ${String(res.status)}.`;
}

// ─── /api/notams/board ────────────────────────────────────────────────

export async function fetchNotamBoard(): Promise<FetchBoardResult> {
  let res: Response;
  try {
    res = await fetch('/api/notams/board', { credentials: 'include' });
  } catch (err) {
    console.error('[notams] fetch network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) {
    return {
      ok: false,
      message: `El servidor respondió con ${String(res.status)}.`,
    };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  if (
    typeof body !== 'object' ||
    body === null ||
    !Array.isArray((body as NotamBoardResponse).notams) ||
    !Array.isArray((body as NotamBoardResponse).postits)
  ) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body as NotamBoardResponse };
}

// ─── POST /api/notam-postits ──────────────────────────────────────────

export async function createNotamPostit(
  payload: NotamPostitPayload,
): Promise<PostitResult> {
  let res: Response;
  try {
    res = await fetch('/api/notam-postits', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[notams] create network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | { postit: NotamPostit }
    | null;
  if (!body?.postit) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body.postit };
}

// ─── PATCH /api/notam-postits/:id ─────────────────────────────────────

export async function updateNotamPostit(
  id: string,
  payload: NotamPostitPayload,
): Promise<PostitResult> {
  let res: Response;
  try {
    res = await fetch(`/api/notam-postits/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[notams] update network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | { postit: NotamPostit }
    | null;
  if (!body?.postit) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body.postit };
}

// ─── DELETE /api/notam-postits/:id ────────────────────────────────────

export async function deleteNotamPostit(id: string): Promise<DeleteResult> {
  let res: Response;
  try {
    res = await fetch(`/api/notam-postits/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    console.error('[notams] delete network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok && res.status !== 204) {
    return { ok: false, message: await readError(res) };
  }
  return { ok: true };
}

// ─── GET /api/notams (admin+) — lista TODOS incluido archivados ──────

export type FetchNotamsResult =
  | { ok: true; data: Notam[] }
  | { ok: false; message: string };

export async function fetchAllNotams(): Promise<FetchNotamsResult> {
  let res: Response;
  try {
    res = await fetch('/api/notams', { credentials: 'include' });
  } catch (err) {
    console.error('[notams] fetch-all network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | { notams: Notam[] }
    | null;
  if (!body || !Array.isArray(body.notams)) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body.notams };
}

// ─── POST/PATCH/DELETE /api/notams (admin+) ───────────────────────────

export async function createNotam(payload: NotamPayload): Promise<NotamResult> {
  let res: Response;
  try {
    res = await fetch('/api/notams', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[notams] create-notam network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | { notam: Notam }
    | null;
  if (!body?.notam) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body.notam };
}

export async function updateNotam(
  id: string,
  payload: NotamPayload,
): Promise<NotamResult> {
  let res: Response;
  try {
    res = await fetch(`/api/notams/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[notams] update-notam network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | { notam: Notam }
    | null;
  if (!body?.notam) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body.notam };
}

export async function deleteNotam(id: string): Promise<DeleteResult> {
  let res: Response;
  try {
    res = await fetch(`/api/notams/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    console.error('[notams] delete-notam network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok && res.status !== 204) {
    return { ok: false, message: await readError(res) };
  }
  return { ok: true };
}

// ─── GET /api/pilots ──────────────────────────────────────────────────

/**
 * Lista pública de pilotos. La usa el editor de post-its para poblar
 * el dropdown de destinatario.
 */
export async function fetchPilots(): Promise<FetchPilotsResult> {
  let res: Response;
  try {
    res = await fetch('/api/pilots', { credentials: 'include' });
  } catch (err) {
    console.error('[notams] pilots network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | { pilots: PilotSummary[] }
    | null;
  if (!body || !Array.isArray(body.pilots)) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body.pilots };
}
